import { NavigateFunction } from 'react-router-dom'

import { CommonPaths } from '@utils/path'

export interface SumsubAccessTokenData {
  userId: string
  token: string
}

export enum SumsubMessage {
  Submitted = 'idCheck.onApplicantSubmitted',
  StepCompleted = 'idCheck.onStepCompleted',
  StatusChanged = 'idCheck.onApplicantStatusChanged',
  Error = 'idCheck.onError',
}

export interface SumsubApplicantState {
  reviewAnswer: SumsubStatusAnswer
  type: SumsubWebhookType
  reviewRejectType: SumsubReviewRejectType
  reviewStatus: SumsubReviewStatus
  createdAt: string
  rejectLabels?: string[]
  buttonIds?: string[]
}

export interface SocketSumsubMessage {
  CreatedAt: string
  ReviewRejectType: SumsubReviewRejectType
  ReviewStatus: SumsubReviewStatus
  Type: SumsubWebhookType
  reviewAnswer: SumsubStatusAnswer
  ButtonIds?: string[]
  RejectLabels?: string[]
}

const SumsubMismatchButtonsIds = ['dataMismatch_fullName', 'dataMismatch_dateOfBirth']
const SumsubMismatchRejectLabels = ['PROBLEMATIC_APPLICANT_DATA']

export const handleSumsubMessages = (
  navigate: NavigateFunction,
  messageData: SocketSumsubMessage | SumsubApplicantState
): void => {
  let sanitizedData = {} as SumsubApplicantState

  if ('Type' in messageData) {
    sanitizedData.type = messageData.Type
    sanitizedData.reviewRejectType = messageData.ReviewRejectType
    sanitizedData.reviewStatus = messageData.ReviewStatus
    sanitizedData.reviewAnswer = messageData.reviewAnswer
    sanitizedData.createdAt = messageData.CreatedAt
    sanitizedData.rejectLabels = messageData.RejectLabels
    sanitizedData.buttonIds = messageData.ButtonIds
  } else {
    sanitizedData = messageData
  }

  if (
    sanitizedData?.reviewAnswer === SumsubStatusAnswer.Green &&
    sanitizedData.type === SumsubWebhookType.ApplicantWorkflowCompleted &&
    sanitizedData.reviewStatus === SumsubReviewStatus.Completed
  ) {
    return navigate(CommonPaths.AccountLoading)
  }

  if (
    SumsubMismatchRejectLabels.some((label) => sanitizedData?.rejectLabels?.includes(label)) &&
    SumsubMismatchButtonsIds.some((id) => sanitizedData?.buttonIds?.includes(id)) &&
    sanitizedData.type === SumsubWebhookType.ApplicantReviewed &&
    sanitizedData.reviewStatus === SumsubReviewStatus.Completed
  ) {
    return navigate(CommonPaths.AccountPending)
  }
}

export enum SumsubStatusAnswer {
  Red = 'RED',
  Green = 'GREEN',
}

/** https://docs.sumsub.com/reference/get-applicant-review-status#review-statuses:~:text=Final%20rejection%20clarification.-,reviewRejectType,-String */
export enum SumsubReviewRejectType {
  Final = 'FINAL',
  Retry = 'RETRY',
  None = '',
}

/** https://docs.sumsub.com/reference/get-applicant-review-status#review-statuses */
export enum SumsubReviewStatus {
  Init = 'init',
  Pending = 'pending',
  Prechecked = 'prechecked',
  Queued = 'queued',
  Completed = 'completed',
  OnHold = 'onHold',
}

/** https://docs.sumsub.com/docs/user-verification-webhooks#applicantworkflowcompleted */
export enum SumsubWebhookType {
  ApplicantCreated = 'applicantCreated',
  ApplicantPending = 'applicantPending',
  ApplicantReviewed = 'applicantReviewed',
  ApplicantOnHold = 'applicantOnHold',
  ApplicantActionPending = 'applicantActionPending',
  ApplicantActionReviewed = 'applicantActionReviewed',
  ApplicantActionOnHold = 'applicantActionOnHold',
  ApplicantPersonalInfoChanged = 'applicantPersonalInfoChanged',
  ApplicantTagsChanged = 'applicantTagsChanged',
  ApplicantActivated = 'applicantActivated',
  ApplicantDeactivated = 'applicantDeactivated',
  ApplicantDeleted = 'applicantDeleted',
  ApplicantReset = 'applicantReset',
  ApplicantPrechecked = 'applicantPrechecked',
  ApplicantLevelChanged = 'applicantLevelChanged',
  /** This one means the workflow is done, and we should show the loading screen */
  ApplicantWorkflowCompleted = 'applicantWorkflowCompleted',
  ApplicantWorkflowFailed = 'applicantWorkflowFailed',
  VideoIdentStatusChanged = 'videoIdentStatusChanged',
  VideoIdentCompositionCompleted = 'videoIdentCompositionCompleted',
}
